import { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { BrowserProvider, ContractFactory, parseUnits } from 'ethers';
import { Wallet, Zap, CheckCircle, AlertTriangle, Copy, ExternalLink } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import theme from '../theme';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const POLYGON_AMOY = { chainId: '0x13882', chainIdBig: 80002n, name: 'Polygon Amoy', rpc: 'https://rpc-amoy.polygon.technology', explorer: 'https://amoy.polygonscan.com' };
// Amoy RPC enforces a minimum priority fee (~25 Gwei) — floor the client submission at 30 Gwei.
const AMOY_PRIORITY_FLOOR = parseUnits('30', 'gwei');
// Amoy RPC also enforces a per-transaction gas cap (33,554,432 observed). Keep client
// submissions a hair under that so a legitimately large constructor still passes.
const AMOY_TX_GAS_CAP = 33000000n;
const AMOY_GAS_STATION_URL = 'https://gasstation.polygon.technology/amoy';

// Translate known fee / rejection RPC errors into Creator-facing messages.
function friendlyDeployError(err) {
  if (!err) return 'Deployment failed';
  if (err.code === 'ACTION_REJECTED' || err.code === 4001) return 'Transaction rejected in wallet.';
  const raw = (err.error?.message || err.data?.message || err.shortMessage || err.message || '').toString();
  const low = raw.toLowerCase();
  if (low.includes('gas tip cap') && low.includes('minimum')) {
    return 'Polygon Amoy rejected the deployment because the network fee was below its current minimum. Refresh the fee estimate and try again.';
  }
  if (low.includes('transaction gas limit too high')) {
    return 'Polygon Amoy rejected the deployment because the transaction gas limit exceeded the network maximum. Kickback Contract has adjusted the deployment gas limit. Please try again.';
  }
  if (low.includes('transaction gas price') && low.includes('below') && low.includes('minimum')) {
    return 'Polygon Amoy rejected the deployment because the gas price was below its current minimum. Refresh the fee estimate and try again.';
  }
  if (low.includes('replacement transaction underpriced')) {
    return 'A previous pending transaction has a higher fee. Wait for it to confirm, or cancel it in your wallet, then retry.';
  }
  if (low.includes('insufficient funds')) {
    return 'Insufficient POL in your wallet to pay the Amoy deployment fee. Top up test POL and try again.';
  }
  return raw.substring(0, 180) || 'Deployment failed';
}

function detectStandard(code, templateId) {
  if (templateId?.includes('erc20') || code?.includes('ERC20.sol')) return 'ERC20';
  if (templateId?.includes('erc1155') || code?.includes('ERC1155.sol')) return 'ERC1155';
  return 'ERC721';
}

const STANDARD_COLORS = { ERC721: '#A855F7', ERC1155: '#3B82F6', ERC20: '#10B981' };

export default function ContractDeployPage({ user }) {
  const { id: draftId } = useParams();
  const [draft, setDraft] = useState(null);
  const [loading, setLoading] = useState(true);

  // Wallet
  const [walletAddr, setWalletAddr] = useState('');
  const [chainOk, setChainOk] = useState(false);
  const [connecting, setConnecting] = useState(false);

  // Compile
  const [compiled, setCompiled] = useState(null);
  const [compiling, setCompiling] = useState(false);

  // Deploy
  const [deploying, setDeploying] = useState(false);
  const [deployResult, setDeployResult] = useState(null);
  const [gasEstimate, setGasEstimate] = useState(null);
  const [verifyError, setVerifyError] = useState(null);
  const [retrying, setRetrying] = useState(false);
  const [reconcileTx, setReconcileTx] = useState('');
  const [reconcileAddr, setReconcileAddr] = useState('');
  const [reconciling, setReconciling] = useState(false);

  const standard = draft ? detectStandard(draft.generated_code, draft.template_id) : '';
  const stdColor = STANDARD_COLORS[standard] || '#6366F1';

  useEffect(() => { fetchDraft(); }, [draftId]);

  const fetchDraft = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/contracts/drafts/${draftId}`, { headers: { Authorization: `Bearer ${token}` } });
      setDraft(res.data.draft);
      if (res.data.draft?.deployed) {
        setDeployResult({
          address: res.data.draft.deployed_address || res.data.draft.contract_address,
          txHash: res.data.draft.deploy_tx_hash || res.data.draft.tx_hash,
          chain: res.data.draft.deployed_chain || res.data.draft.chain,
        });
      }
    } catch (err) {
      toast.error('Failed to load contract draft');
    } finally {
      setLoading(false);
    }
  };

  const checkChain = useCallback(async () => {
    if (!window.ethereum) return;
    try {
      const cid = await window.ethereum.request({ method: 'eth_chainId' });
      setChainOk(cid === POLYGON_AMOY.chainId);
    } catch { setChainOk(false); }
  }, []);

  const connectWallet = async () => {
    if (!window.ethereum) { toast.error('No wallet detected. Install MetaMask or Coinbase Wallet.'); return; }
    setConnecting(true);
    try {
      const provider = new BrowserProvider(window.ethereum);
      const accounts = await provider.send('eth_requestAccounts', []);
      setWalletAddr(accounts[0]);
      await checkChain();
      window.ethereum.on('chainChanged', checkChain);
      window.ethereum.on('accountsChanged', (accs) => setWalletAddr(accs[0] || ''));
      toast.success('Wallet connected');
    } catch (err) {
      toast.error(err.message || 'Connection failed');
    } finally {
      setConnecting(false);
    }
  };

  const switchNetwork = async () => {
    try {
      await window.ethereum.request({
        method: 'wallet_switchEthereumChain',
        params: [{ chainId: POLYGON_AMOY.chainId }],
      });
      setChainOk(true);
      toast.success('Switched to Polygon Amoy');
    } catch (err) {
      if (err.code === 4902) {
        try {
          await window.ethereum.request({
            method: 'wallet_addEthereumChain',
            params: [{
              chainId: POLYGON_AMOY.chainId, chainName: POLYGON_AMOY.name,
              nativeCurrency: { name: 'POL', symbol: 'POL', decimals: 18 },
              rpcUrls: [POLYGON_AMOY.rpc], blockExplorerUrls: [POLYGON_AMOY.explorer],
            }],
          });
          setChainOk(true);
        } catch (addErr) {
          toast.error('Failed to add Polygon Amoy network');
        }
      } else {
        toast.error('Failed to switch network');
      }
    }
  };

  const handleCompile = async () => {
    if (!draft?.generated_code) return;
    setCompiling(true);
    setCompiled(null);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API}/contracts/compile`, { solidity_code: draft.generated_code }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      // Guard against the structured failure envelope the backend returns on
      // compile errors / toolchain issues (`{success:false, stage, error_type,
      // message, compiler_errors[]}`). Storing it in `compiled` would crash
      // the UI at `compiled.abi.length` because the envelope has no `abi`.
      const data = res.data || {};
      if (data.success === false || !Array.isArray(data.abi) || typeof data.bytecode !== 'string') {
        const errMsg =
          (Array.isArray(data.compiler_errors) && data.compiler_errors[0]) ||
          data.message || 'Compilation failed';
        toast.error(`Compile failed: ${String(errMsg).substring(0, 200)}`);
        return;
      }
      setCompiled(data);
      toast.success(`Compiled: ${data.contract_name}`);

      // Estimate gas if wallet connected
      if (walletAddr && chainOk && window.ethereum) {
        try {
          const provider = new BrowserProvider(window.ethereum);
          const signer = await provider.getSigner();
          const factory = new ContractFactory(data.abi, data.bytecode, signer);
          const deployTx = await factory.getDeployTransaction();
          const est = await provider.estimateGas(deployTx);
          setGasEstimate(est.toString());
        } catch { /* gas estimate optional */ }
      }
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (typeof detail === 'object' && detail && (detail.compiler_errors || detail.details)) {
        const first = (detail.compiler_errors && detail.compiler_errors[0]) || (detail.details && detail.details[0]) || detail.message;
        toast.error(`Compile failed: ${String(first).substring(0, 200)}`);
      } else {
        toast.error(typeof detail === 'string' ? detail : 'Compilation failed');
      }
    } finally {
      setCompiling(false);
    }
  };

  const handleDeploy = async () => {
    if (!compiled || !walletAddr || !chainOk) return;
    setDeploying(true);
    setVerifyError(null);
    try {
      const provider = new BrowserProvider(window.ethereum);
      const signer = await provider.getSigner();
      const factory = new ContractFactory(compiled.abi, compiled.bytecode, signer);

      // ------------------------------------------------------------------
      // EIP-1559 fee construction for Polygon Amoy.
      // Amoy's RPC rejects transactions whose priority tip is below ~25 Gwei
      // (MetaMask historically defaults to 1.5 Gwei). We derive a safe
      // dynamic maxPriorityFeePerGas from (a) the Polygon Gas Station,
      // (b) provider feeData, (c) a 30 Gwei floor — whichever is highest.
      // maxFeePerGas = max(provider.maxFeePerGas, baseFee*2 + priority).
      // All of this is just a transaction *proposal*; MetaMask still prompts
      // the Creator to review and click Confirm. No private key or seed
      // phrase ever touches this code.
      // ------------------------------------------------------------------
      const network = await provider.getNetwork();
      const chainId = network.chainId;
      const feeData = await provider.getFeeData();
      const latestBlock = await provider.getBlock('latest');

      let maxPriorityFeePerGas = feeData.maxPriorityFeePerGas || null;
      let gasStationPriority = null;
      if (chainId === POLYGON_AMOY.chainIdBig) {
        try {
          const response = await fetch(AMOY_GAS_STATION_URL);
          if (response.ok) {
            const gasStation = await response.json();
            if (gasStation?.standard?.maxPriorityFee != null) {
              gasStationPriority = parseUnits(gasStation.standard.maxPriorityFee.toString(), 'gwei');
              if (!maxPriorityFeePerGas || gasStationPriority > maxPriorityFeePerGas) {
                maxPriorityFeePerGas = gasStationPriority;
              }
            }
          }
        } catch (gasErr) {
          console.warn('Amoy gas station unavailable; using provider/fallback fee data.', gasErr);
        }
        if (!maxPriorityFeePerGas || maxPriorityFeePerGas < AMOY_PRIORITY_FLOOR) {
          maxPriorityFeePerGas = AMOY_PRIORITY_FLOOR;
        }
      } else if (!maxPriorityFeePerGas) {
        maxPriorityFeePerGas = parseUnits('2', 'gwei');
      }

      const baseFeePerGas = latestBlock?.baseFeePerGas ?? 0n;
      const calculatedMaxFee = baseFeePerGas > 0n
        ? baseFeePerGas * 2n + maxPriorityFeePerGas
        : maxPriorityFeePerGas * 2n;
      const maxFeePerGas = (feeData.maxFeePerGas && feeData.maxFeePerGas > calculatedMaxFee)
        ? feeData.maxFeePerGas
        : calculatedMaxFee;

      // Gas limit — estimate from the unsigned deployment transaction, apply a
      // 10% buffer, then cap below Polygon Amoy's per-transaction RPC ceiling
      // (observed hard cap 33,554,432). We reject locally before MetaMask if
      // the raw estimate itself exceeds the safe cap — this avoids a wasted
      // signing round trip and gives the Creator a readable toast immediately.
      const deployTx = await factory.getDeployTransaction();
      const estimatedGas = await provider.estimateGas({ ...deployTx, from: walletAddr });
      const bufferedGas = (estimatedGas * 110n) / 100n;
      let gasLimit = bufferedGas;
      if (chainId === POLYGON_AMOY.chainIdBig) {
        if (estimatedGas > AMOY_TX_GAS_CAP) {
          throw new Error(
            `Estimated deployment gas (${estimatedGas.toString()}) exceeds the Polygon Amoy safe transaction limit (${AMOY_TX_GAS_CAP.toString()}).`
          );
        }
        gasLimit = bufferedGas > AMOY_TX_GAS_CAP ? AMOY_TX_GAS_CAP : bufferedGas;
      }

      toast.info('Confirm transaction in your wallet...');
      const contract = await factory.deploy({ gasLimit, maxFeePerGas, maxPriorityFeePerGas });
      toast.info('Transaction sent. Waiting for confirmation...');
      const receipt = await contract.deploymentTransaction().wait();
      const address = await contract.getAddress();
      const txHash = receipt.hash;

      // Record on backend — backend verifies on-chain before persisting
      const token = localStorage.getItem('token');
      try {
        const res = await axios.post(`${API}/contracts/drafts/${draftId}/deploy-record`, {
          chain: 'polygon-amoy', address, tx_hash: txHash,
        }, { headers: { Authorization: `Bearer ${token}` } });

        setDeployResult({ address, txHash, chain: 'polygon-amoy', verifiedBlock: res.data.verified_block });
        toast.success('Contract deployed and verified on-chain!');
      } catch (backendErr) {
        const detail = backendErr.response?.data?.detail || 'Backend verification failed';
        toast.error(`Verification rejected: ${detail}`);
        setVerifyError({ address, txHash, chain: 'polygon-amoy', error: detail });
      }
    } catch (err) {
      toast.error(friendlyDeployError(err));
    } finally {
      setDeploying(false);
    }
  };

  const copyText = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied!');
  };

  // Retry verification against the EXISTING successful transaction. Never
  // opens MetaMask and never calls factory.deploy() — only re-asks the
  // backend to reconcile the already-settled Amoy transaction.
  const persistExistingDeployment = async (txHash, address) => {
    const token = localStorage.getItem('token');
    const res = await axios.post(`${API}/contracts/drafts/${draftId}/deploy-record`, {
      chain: 'polygon-amoy', address, tx_hash: txHash,
    }, { headers: { Authorization: `Bearer ${token}` } });
    setDeployResult({ address, txHash, chain: 'polygon-amoy', verifiedBlock: res.data.verified_block });
    setVerifyError(null);
    toast.success(res.data.already_deployed ? 'Deployment already on record.' : 'Contract deployment verified on-chain!');
  };

  const handleRetryVerification = async () => {
    if (!verifyError?.txHash || !verifyError?.address) return;
    setRetrying(true);
    try {
      await persistExistingDeployment(verifyError.txHash, verifyError.address);
    } catch (err) {
      const detail = err.response?.data?.detail || 'Verification retry failed';
      toast.error(typeof detail === 'string' ? detail : 'Verification retry failed');
    } finally {
      setRetrying(false);
    }
  };

  const handleReconcileExisting = async () => {
    const tx = (reconcileTx || '').trim();
    const addr = (reconcileAddr || '').trim();
    if (!/^0x[0-9a-fA-F]{64}$/.test(tx)) { toast.error('Transaction hash must be 0x + 64 hex chars.'); return; }
    if (!/^0x[0-9a-fA-F]{40}$/.test(addr)) { toast.error('Contract address must be 0x + 40 hex chars.'); return; }
    setReconciling(true);
    try {
      await persistExistingDeployment(tx, addr);
      setReconcileTx(''); setReconcileAddr('');
    } catch (err) {
      const detail = err.response?.data?.detail || 'Reconciliation failed';
      toast.error(typeof detail === 'string' ? detail : 'Reconciliation failed');
    } finally {
      setReconciling(false);
    }
  };

  if (loading) return <div style={{ padding: '32px', color: theme.colors.text.muted }}>Loading...</div>;
  if (!draft) return <div style={{ padding: '32px', color: theme.colors.text.muted }}>Draft not found</div>;

  return (
    <div data-testid="contract-deploy-page" style={{ maxWidth: '900px' }}>
      {/* Header */}
      <div style={{ marginBottom: '32px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
          <h1 style={{ fontFamily: theme.fonts.heading, fontSize: '28px', fontWeight: '700', color: theme.colors.text.primary }}>
            Deploy Contract
          </h1>
          <span data-testid="contract-type-badge" style={{
            padding: '4px 12px', borderRadius: '999px', fontSize: '12px', fontWeight: '700',
            background: `${stdColor}15`, color: stdColor, border: `1px solid ${stdColor}40`,
          }}>{standard}</span>
        </div>
        <p style={{ color: theme.colors.text.secondary, fontSize: '15px' }}>
          {draft.template_name || draft.standard || 'Contract'} &middot; Polygon Amoy Testnet
        </p>
      </div>

      {/* Deploy Success */}
      {deployResult && (
        <Card data-testid="deploy-success-panel" style={{ border: `2px solid #10B981`, borderRadius: theme.radius.card, marginBottom: '24px', background: 'rgba(16, 185, 129, 0.05)' }}>
          <CardContent className="pt-5 pb-5">
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px' }}>
              <CheckCircle size={24} color="#10B981" />
              <p style={{ fontSize: '18px', fontWeight: '700', color: '#10B981' }}>Contract Deployed & Verified</p>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <div>
                <p style={{ fontSize: '12px', color: theme.colors.text.muted, marginBottom: '4px' }}>Contract Address</p>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <code data-testid="deployed-address" style={{ fontSize: '14px', color: theme.colors.text.primary, fontFamily: 'monospace', wordBreak: 'break-all' }}>
                    {deployResult.address}
                  </code>
                  <button onClick={() => copyText(deployResult.address)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px' }}>
                    <Copy size={14} color={theme.colors.text.muted} />
                  </button>
                  <a href={`${POLYGON_AMOY.explorer}/address/${deployResult.address}`} target="_blank" rel="noreferrer" style={{ display: 'flex' }}>
                    <ExternalLink size={14} color={theme.colors.primary} />
                  </a>
                </div>
              </div>
              {deployResult.txHash && (
                <div>
                  <p style={{ fontSize: '12px', color: theme.colors.text.muted, marginBottom: '4px' }}>Transaction Hash</p>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <code data-testid="deployed-tx-hash" style={{ fontSize: '13px', color: theme.colors.text.secondary, fontFamily: 'monospace', wordBreak: 'break-all' }}>
                      {deployResult.txHash}
                    </code>
                    <button onClick={() => copyText(deployResult.txHash)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px' }}>
                      <Copy size={14} color={theme.colors.text.muted} />
                    </button>
                  </div>
                </div>
              )}
              {deployResult.verifiedBlock && (
                <p style={{ fontSize: '12px', color: '#10B981' }}>Verified at block: {parseInt(deployResult.verifiedBlock, 16)}</p>
              )}
              <p style={{ fontSize: '12px', color: theme.colors.text.muted }}>Chain: {deployResult.chain}</p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Verify Tx Error Panel */}
      {verifyError && !deployResult && (
        <Card data-testid="verify-error-panel" style={{ border: '2px solid #FFC107', borderRadius: theme.radius.card, marginBottom: '24px', background: 'rgba(255, 193, 7, 0.06)' }}>
          <CardContent className="pt-5 pb-5">
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '12px' }}>
              <AlertTriangle size={24} color="#FFC107" />
              <p style={{ fontSize: '18px', fontWeight: '700', color: '#FFC107' }}>Deployment transaction confirmed. Verification is still being completed.</p>
            </div>
            <p style={{ fontSize: '13px', color: theme.colors.text.secondary, marginBottom: '12px', lineHeight: '1.5' }}>
              Your transaction was signed and sent to Polygon Amoy. The backend could not finalize on-chain verification yet (temporary API/RPC issue). Your deployment is <strong>not lost</strong> — click Retry Verification to reconcile the existing transaction. Do not sign a new deployment.
            </p>
            <p data-testid="verify-error-msg" style={{ fontSize: '12px', color: theme.colors.text.muted, marginBottom: '12px', padding: '8px', background: 'rgba(0,0,0,0.2)', borderRadius: '6px', fontFamily: 'monospace' }}>
              {verifyError.error}
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '14px' }}>
              <div>
                <p style={{ fontSize: '11px', color: theme.colors.text.muted }}>Submitted TX Hash</p>
                <code style={{ fontSize: '12px', color: theme.colors.text.secondary, fontFamily: 'monospace', wordBreak: 'break-all' }}>{verifyError.txHash}</code>
              </div>
              <div>
                <p style={{ fontSize: '11px', color: theme.colors.text.muted }}>Submitted Address</p>
                <code style={{ fontSize: '12px', color: theme.colors.text.secondary, fontFamily: 'monospace', wordBreak: 'break-all' }}>{verifyError.address}</code>
              </div>
              <div>
                <p style={{ fontSize: '11px', color: theme.colors.text.muted }}>Chain</p>
                <code style={{ fontSize: '12px', color: theme.colors.text.secondary }}>{verifyError.chain}</code>
              </div>
            </div>
            <Button data-testid="retry-verification-btn" onClick={handleRetryVerification} disabled={retrying}
              style={{ width: '100%', background: `linear-gradient(135deg, #10B981, #06B6D4)`, color: '#FFF', fontWeight: '700' }}>
              {retrying ? 'Verifying…' : 'Retry Verification'}
            </Button>
            <p style={{ fontSize: '11px', color: theme.colors.text.muted, marginTop: '8px', textAlign: 'center' }}>
              This does NOT open your wallet or create a new transaction. It only reconciles the existing on-chain tx with Kickback Contract.
            </p>
            <div style={{ marginTop: '14px', padding: '10px', background: 'rgba(255,193,7,0.08)', borderRadius: '6px', border: '1px solid rgba(255,193,7,0.18)' }}>
              <p style={{ fontSize: '12px', color: '#FFC107' }}>
                You can also open <a href={`${POLYGON_AMOY.explorer}/tx/${verifyError.txHash}`} target="_blank" rel="noreferrer" style={{ color: theme.colors.primary, textDecoration: 'underline' }}>your tx on Polygonscan</a> while the retry runs.
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 340px', gap: '24px' }}>
        {/* Solidity Code */}
        <Card style={{ border: `1px solid ${theme.colors.borders}`, borderRadius: theme.radius.card }}>
          <CardHeader>
            <CardTitle style={{ color: theme.colors.text.primary, fontSize: '15px' }}>Solidity Source</CardTitle>
          </CardHeader>
          <CardContent>
            <pre data-testid="solidity-code-box" style={{
              background: '#1a1a2e', color: '#e0e0e0', padding: '16px', borderRadius: '8px',
              fontSize: '12px', lineHeight: '1.6', maxHeight: '400px', overflow: 'auto', whiteSpace: 'pre-wrap', wordBreak: 'break-word',
            }}>
              {draft.generated_code}
            </pre>
          </CardContent>
        </Card>

        {/* Deploy Controls */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          {/* Step 1: Connect Wallet */}
          <Card style={{ border: `1px solid ${theme.colors.borders}`, borderRadius: theme.radius.card }}>
            <CardContent className="pt-4 pb-4">
              <p style={{ fontWeight: '600', color: theme.colors.text.primary, fontSize: '14px', marginBottom: '10px' }}>1. Connect Wallet</p>
              {!walletAddr ? (
                <Button data-testid="connect-wallet-btn" onClick={connectWallet} disabled={connecting}
                  style={{ width: '100%', background: `linear-gradient(135deg, ${stdColor}, #6366F1)`, color: '#FFF', fontWeight: '600' }}>
                  <Wallet size={16} style={{ marginRight: '6px' }} /> {connecting ? 'Connecting...' : 'Connect Wallet'}
                </Button>
              ) : (
                <div>
                  <p style={{ fontSize: '12px', color: theme.colors.text.muted }}>Connected:</p>
                  <code data-testid="wallet-address" style={{ fontSize: '12px', color: '#10B981', wordBreak: 'break-all' }}>
                    {walletAddr.slice(0, 6)}...{walletAddr.slice(-4)}
                  </code>
                </div>
              )}
              {!window.ethereum && (
                <p style={{ fontSize: '12px', color: '#FF6B6B', marginTop: '8px' }}>
                  No wallet detected. Install MetaMask or Coinbase Wallet.
                </p>
              )}
            </CardContent>
          </Card>

          {/* Step 2: Check Network */}
          <Card style={{ border: `1px solid ${theme.colors.borders}`, borderRadius: theme.radius.card }}>
            <CardContent className="pt-4 pb-4">
              <p style={{ fontWeight: '600', color: theme.colors.text.primary, fontSize: '14px', marginBottom: '10px' }}>2. Network</p>
              {!walletAddr ? (
                <p style={{ fontSize: '13px', color: theme.colors.text.muted }}>Connect wallet first</p>
              ) : chainOk ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <CheckCircle size={16} color="#10B981" />
                  <span style={{ fontSize: '13px', color: '#10B981', fontWeight: '500' }}>Polygon Amoy</span>
                </div>
              ) : (
                <div>
                  <div data-testid="wrong-network-warning" style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '8px' }}>
                    <AlertTriangle size={16} color="#FFC107" />
                    <span style={{ fontSize: '13px', color: '#FFC107' }}>Wrong network</span>
                  </div>
                  <Button data-testid="switch-network-btn" onClick={switchNetwork} size="sm"
                    style={{ width: '100%', background: 'rgba(255,193,7,0.15)', color: '#FFC107', fontSize: '13px' }}>
                    Switch to Polygon Amoy
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Step 3: Compile */}
          <Card style={{ border: `1px solid ${theme.colors.borders}`, borderRadius: theme.radius.card }}>
            <CardContent className="pt-4 pb-4">
              <p style={{ fontWeight: '600', color: theme.colors.text.primary, fontSize: '14px', marginBottom: '10px' }}>3. Compile</p>
              <Button data-testid="compile-btn" onClick={handleCompile} disabled={compiling || !draft?.generated_code}
                style={{ width: '100%', background: compiled?.contract_name ? 'rgba(16,185,129,0.15)' : `rgba(99,102,241,0.15)`, color: compiled?.contract_name ? '#10B981' : '#6366F1', fontWeight: '600' }}>
                <Zap size={16} style={{ marginRight: '6px' }} />
                {compiling ? 'Compiling...' : compiled?.contract_name ? `Compiled: ${compiled.contract_name}` : 'Compile Contract'}
              </Button>
              {compiled && Array.isArray(compiled.abi) && typeof compiled.bytecode === 'string' && (
                <p style={{ fontSize: '11px', color: theme.colors.text.muted, marginTop: '6px' }}>
                  ABI: {compiled.abi.length} functions &middot; Bytecode: {(compiled.bytecode.length / 2).toLocaleString()} bytes
                </p>
              )}
            </CardContent>
          </Card>

          {/* Step 4: Deploy */}
          <Card style={{ border: `1px solid ${theme.colors.borders}`, borderRadius: theme.radius.card }}>
            <CardContent className="pt-4 pb-4">
              <p style={{ fontWeight: '600', color: theme.colors.text.primary, fontSize: '14px', marginBottom: '10px' }}>4. Deploy</p>
              {gasEstimate && (
                <p style={{ fontSize: '11px', color: theme.colors.text.muted, marginBottom: '8px' }}>
                  Estimated gas: {parseInt(gasEstimate).toLocaleString()} units
                </p>
              )}
              <p style={{ fontSize: '11px', color: '#FFC107', marginBottom: '10px', lineHeight: '1.4' }}>
                You pay gas fees from your connected wallet. This deploys to Polygon Amoy testnet.
              </p>
              <Button data-testid="deploy-btn" onClick={handleDeploy}
                disabled={deploying || !compiled || !walletAddr || !chainOk || !!deployResult || !!verifyError}
                style={{
                  width: '100%', fontWeight: '700', padding: '12px',
                  background: (!compiled || !walletAddr || !chainOk || !!deployResult || !!verifyError) ? 'rgba(99,102,241,0.1)' : `linear-gradient(135deg, #10B981, #06B6D4)`,
                  color: (!compiled || !walletAddr || !chainOk || !!deployResult || !!verifyError) ? theme.colors.text.muted : '#FFF',
                }}>
                {deploying ? 'Deploying...' : deployResult ? 'Deployed & Verified' : verifyError ? 'Transaction already submitted — use Retry Verification' : 'Deploy to Polygon Amoy'}
              </Button>
              {/* P10.6.3: Disabled-reason explainer */}
              {!deployResult && (
                <p data-testid="deploy-disabled-reason" style={{ fontSize: '11px', color: theme.colors.text.muted, marginTop: '8px', lineHeight: '1.5' }}>
                  {!walletAddr && 'Disabled: connect your wallet first.'}
                  {walletAddr && !chainOk && 'Disabled: wrong network. Switch to Polygon Amoy.'}
                  {walletAddr && chainOk && !compiled && 'Disabled: compile the contract first.'}
                  {walletAddr && chainOk && compiled && !deploying && 'Ready to deploy.'}
                </p>
              )}
              {/* P10.6.3: Test POL inline guidance */}
              <p data-testid="need-test-pol" style={{ fontSize: '11px', color: theme.colors.text.muted, marginTop: '6px' }}>
                Need test POL?{' '}
                <a href="https://faucet.polygon.technology/" target="_blank" rel="noreferrer"
                  style={{ color: theme.colors.primary, textDecoration: 'underline' }}>
                  click here
                </a>{' '}
                (Polygon Amoy faucet).
              </p>
            </CardContent>
          </Card>

          {/* Reconcile Existing Deployment — recovers a successful on-chain
              transaction when the Deploy page lost its post-deploy state
              (page refresh, Cloudflare/origin blip, closed tab, etc.). This
              NEVER opens the wallet or sends a new transaction. */}
          {!deployResult && (
            <Card data-testid="reconcile-card" style={{ border: `1px solid ${theme.colors.borders}`, borderRadius: theme.radius.card, background: 'rgba(99, 102, 241, 0.03)' }}>
              <CardContent className="pt-4 pb-4">
                <p style={{ fontWeight: '600', color: theme.colors.text.primary, fontSize: '14px', marginBottom: '4px' }}>
                  Already deployed on Polygon Amoy?
                </p>
                <p style={{ fontSize: '11px', color: theme.colors.text.muted, marginBottom: '10px', lineHeight: '1.5' }}>
                  If your last deploy succeeded on-chain but Kickback Contract didn't finish verification, paste the transaction hash and contract address here to reconcile. No new transaction is sent.
                </p>
                <input data-testid="reconcile-tx-input" value={reconcileTx} onChange={(e) => setReconcileTx(e.target.value)}
                  placeholder="0x… transaction hash (66 chars)"
                  style={{ width: '100%', padding: '8px 10px', fontSize: '12px', fontFamily: 'monospace', border: `1px solid ${theme.colors.borders}`, borderRadius: '6px', background: theme.colors.background, color: theme.colors.text.primary, marginBottom: '8px' }} />
                <input data-testid="reconcile-address-input" value={reconcileAddr} onChange={(e) => setReconcileAddr(e.target.value)}
                  placeholder="0x… contract address (42 chars)"
                  style={{ width: '100%', padding: '8px 10px', fontSize: '12px', fontFamily: 'monospace', border: `1px solid ${theme.colors.borders}`, borderRadius: '6px', background: theme.colors.background, color: theme.colors.text.primary, marginBottom: '10px' }} />
                <Button data-testid="reconcile-btn" onClick={handleReconcileExisting}
                  disabled={reconciling || !reconcileTx || !reconcileAddr}
                  style={{ width: '100%', background: (!reconcileTx || !reconcileAddr) ? 'rgba(99,102,241,0.1)' : 'rgba(16, 185, 129, 0.15)', color: (!reconcileTx || !reconcileAddr) ? theme.colors.text.muted : '#10B981', fontWeight: '600' }}>
                  {reconciling ? 'Reconciling…' : 'Reconcile Existing Deployment'}
                </Button>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}
